import type * as mdast from 'mdast';

import {
    FRAGMENT_ANNOTATION_BODY,
    INLINE_ANNOTATION,
    INLINE_BUTTON,
    INLINE_EMOJI,
    INLINE_EXPRESSION,
    INLINE_ICON,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MATH,
    INLINE_MENTION,
    type InlineExpressionData,
    type InlineImageData,
    images,
} from '@gitbook/doc-core';
import type { Inline } from '@gitbook/slate';
import { emojisByCode } from '@gitbook/util-emojis';

import { footnoteIdToHtmlId } from '../../utils';
import { formatHTML } from '../html';
import type { GastToMdastTransformMap } from './types';

/**
 * Transform inline elements to mdast
 */
export const transformInlines: GastToMdastTransformMap<
    Inline,
    mdast.PhrasingContent | mdast.Literal,
    mdast.PhrasingContent
> = {
    [INLINE_BUTTON]: async (node, _eat, options) => {
        // We represent buttons as HTML, as there is nothing in the Markdown spec to attach classes or properties to a link.
        return [
            {
                type: 'html',
                value: await formatHTML(node, options),
            },
        ];
    },
    [INLINE_LINK]: async (node, eat, options) => {
        const resolved = await options.resolveContentRef(node.data.get('ref'));
        if (!resolved) {
            return [];
        }
        return [
            {
                type: 'link',
                title: node.data.get('title') || null,
                url: resolved.url,
                children: await eat(node.nodes),
            },
        ];
    },
    [INLINE_MENTION]: async (node, _eat, options) => {
        const ref = node.data.get('ref');
        const resolved = await options.resolveContentRef(ref);
        if (!resolved) {
            return [];
        }

        return [
            {
                type: 'link',
                title: 'mention',
                url: resolved.url,
                children: [
                    {
                        type: 'text',
                        value: resolved.text,
                    },
                ],
            },
        ];
    },
    [INLINE_EMOJI]: (node) => {
        const emoji = emojisByCode[node.data.get('code')];
        if (!emoji) {
            return [];
        }

        return [
            {
                type: 'text',
                value: emoji.shortname,
            },
        ];
    },
    [INLINE_ICON]: async (node) => {
        const iconName = node.data.get('icon');
        if (typeof iconName !== 'string') {
            return [];
        }

        return [
            {
                type: 'html',
                // We put the icon name in the text to make it easier to read on GitHub
                // and also to avoid rehype-minify-whitespace from trimming whitespace around it.
                value: `<i class="fa-${iconName}">:${iconName}:</i>`,
            },
        ];
    },
    [INLINE_EXPRESSION]: (node) => {
        const { expression } = node.data.toFrozenObject() as InlineExpressionData;
        return [{ type: 'html', value: `<code class="expression">${expression}</code>` }];
    },
    [INLINE_IMAGE]: async (node, _eat, options) => {
        const imageData = node.data.toFrozenObject<InlineImageData>();

        if (imageData.size || imageData.refDark) {
            // We use HTML when image can't be represented in Markdown
            return [
                {
                    type: 'html',
                    value: await formatHTML(node, options),
                },
            ];
        }

        const resolvedRef = await options.resolveContentRef(imageData.ref);
        if (!resolvedRef) {
            return [];
        }

        const imgMd: mdast.Image = {
            type: 'image',
            url: resolvedRef.url,
            alt: images.getImageAltText(node),
        };

        return [imgMd];
    },
    [INLINE_MATH]: (node) => {
        const formula = node.data.get('formula').trim();
        if (!formula) {
            return [];
        }

        return [
            {
                type: 'inlineMath',
                value: formula,
            },
        ];
    },
    [INLINE_ANNOTATION]: async (node, eat, context) => {
        const fragment = node.getFragmentByKey(FRAGMENT_ANNOTATION_BODY);
        const children = await eat(node.nodes);

        if (!fragment) {
            return children;
        }

        const footnoteId = context.allocateFootnote(fragment);

        // We use markdown footnote syntax to represent annotations
        // when the annotation is basic (one word, no formatting).
        if (node.getMarks().size === 0 && node.text.match(/^\S+$/)) {
            return [
                ...children,
                {
                    type: 'footnoteReference',
                    identifier: footnoteId,
                },
            ];
        }

        return [
            {
                type: 'link',
                url: `#${footnoteIdToHtmlId(footnoteId)}`,
                children,
            },
            {
                type: 'footnoteReference',
                identifier: footnoteId,
            },
        ];
    },
};
